import { useTranslation } from "react-i18next";

import { renderMilestoneDateByTypeAndReference } from "../../../../../../../../../../components/Generic/MilestoneDates";
import { CartonEvents } from "../../../../../../../../../../models/carton.events";
import { CartonProduct } from "../../../../../../../../../../models/carton.product";
import { MilestoneReference, MilestoneType } from "../../../../../../../../../../models/milestone";
import { ProductStock } from "../../../../../../../../../../models/product.stock";
import { PurchaseOrder } from "../../../../../../../../../../models/purchase.order";
import { Shipment } from "../../../../../../../../../../models/shipments";
import { ColumnWithKey } from "../../../../../../../../../../types";
import { CartonEventType, CronJobType } from "../../../../../../../../../../util/enums";
import { formatDateWithoutTimeZone } from "../../../../../../../../../../util/Format";

export const useProductColumns = () => {
  const { t: translate } = useTranslation();

  const renderStatusByEvent = (status?: string) => {
    if (!status) return translate("noRecord");

    if (status === CartonEventType.PICK_UP) return translate("pickedUp");

    return status;
  }

  const stockColumns: ColumnWithKey[] = [
    {
      name: "cartonProducts",
      title: `${translate("sku")} #`,
      hideSearch: true,
      hideShowColumn: true,
      formatText: (cartonProduct: CartonProduct[]) => cartonProduct?.[0]?.product?.sku || "",
      searchIdentifier: "product.sku",
      key: "sku",
    },
    {
      name: "cartonEvents",
      title: translate("status"),
      formatText: (cartonEvent: CartonEvents[]) =>
        renderStatusByEvent(cartonEvent?.[0]?.eventType?.description),
      hideSearch: true,
      key: "status",
    },
    {
      name: "shipments",
      title: translate("project"),
      formatText: (shipments: Shipment[]) => shipments?.[0].project?.name || "",
      hideSearch: true,
      key: "project",
    },
    { name: "comment", title: translate("comment"), hideSearch: true },
    {
      name: "cartonEvents",
      title: translate("cartonCondition"),
      formatText: (cartonEvent: CartonEvents[]) => cartonEvent?.[0]?.cartonCondition || "",
      hideSearch: true,
      key: "cartonCondition",
    },
    {
      name: "cartonEvents",
      title: translate("eventDate"),
      formatText: (cartonEvent: CartonEvents[]) =>
        formatDateWithoutTimeZone(cartonEvent?.[0]?.eventDate) || "",
      key: "eventDate",
    },
    {
      name: "shipments",
      title: translate("hawb"),
      formatText: (shipments: Shipment[]) => shipments?.[0]?.hwab || "",
      key: "hawb",
    },
    {
      name: "shipments",
      title: `${translate("container")} #`,
      formatText: (shipments: Shipment[]) => shipments?.[0]?.containerId || "",
      key: "container",
    },
    {
      name: "purchaseOrder",
      title: `${translate("order")} #`,
      formatText: (order: PurchaseOrder) => order?.reference || "",
      key: "order",
    },
    {
      name: "shipments",
      title: translate("deliveryStore"),
      formatText: (shipments: Shipment[]) => {
        return shipments?.[0]?.supplier?.name || "";
      },
      key: "deliveryStore",
    },
    {
      name: "shipments",
      title: translate("storeNumber"),
      formatText: (shipments: Shipment[]) => {
        return shipments?.[0]?.supplier?.id || "";
      },
      key: "storeNumber",
    },
    {
      name: "shipments",
      title: translate("type"),
      formatText: (shipments: Shipment[]) => {
        return shipments?.[0]?.shipmentType || "";
      },
      key: "type",
    },
    {
      name: "shipments",
      title: translate("etaAtDestination"),
      formatText: (shipments: Shipment[]) => {
        return renderMilestoneDateByTypeAndReference(
          shipments?.[0]?.milestones || [],
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.DESTINATION
        );
      },
      key: MilestoneReference.SHIPMENT.ESTIMATED.DESTINATION,
    },
    {
      name: "shipments",
      title: translate("estimateArrivalWH"),
      formatText: (shipments: Shipment[]) => {
        return renderMilestoneDateByTypeAndReference(
          shipments?.[0]?.milestones || [],
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.WAREHOUSE
        );
      },
      key: "etaWarehouse",
    },
    {
      name: "shipments",
      title: translate("unpackDate"),
      formatText: (shipments: Shipment[]) => {
        return renderMilestoneDateByTypeAndReference(
          shipments?.[0]?.milestones || [],
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.UNPACK
        );
      },
      key: "unpackDate",
    },
    {
      name: "shipments",
      title: translate("arrivalFinalPort"), // n sei qual é
      formatText: (shipments: Shipment[]) => {
        return renderMilestoneDateByTypeAndReference(
          shipments?.[0]?.milestones || [],
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.DESTINATION
        );
      },
      key: "arrivalFinalPort",
    },
    {
      name: "shipments",
      title: translate("pickUpDate"),
      formatText: (shipments: Shipment[]) => {
        return renderMilestoneDateByTypeAndReference(
          shipments?.[0]?.milestones || [],
          MilestoneType.ACTUAL,
          MilestoneReference.SHIPMENT.ACTUAL.PICKUP_ORIGIN
        );
      },
      key: "pickUpDate",
    },
    {
      name: "shipments",
      title: translate("scanDate"),
      formatText: (shipments: Shipment[]) => {
        return renderMilestoneDateByTypeAndReference(
          shipments?.[0]?.milestones || [],
          MilestoneType.ACTUAL,
          MilestoneReference.SHIPMENT.ACTUAL.SCANNED_DATE
        );
      },
      key: "scanDate",
    },
    {
      name: "shipments",
      title: translate("departureDate"),
      formatText: (shipments: Shipment[]) => {
        return renderMilestoneDateByTypeAndReference(
          shipments?.[0]?.milestones || [],
          MilestoneType.ACTUAL,
          MilestoneReference.SHIPMENT.ACTUAL.DEPARTURE
        );
      },
      key: "departureDate",
    },
    {
      name: "shipments",
      title: translate("arrivalAtDestination"),
      formatText: (shipments: Shipment[]) => {
        return renderMilestoneDateByTypeAndReference(
          shipments?.[0]?.milestones || [],
          MilestoneType.ACTUAL,
          MilestoneReference.SHIPMENT.ACTUAL.ARRIVAL_AT_DESTINATION,
        );
      },
      key: "arrivalAtDestination",
    },
  ];

  const stockOnHandColumns: ColumnWithKey[] = [
    {
      name: "containerNumber",
      title: `${translate("container")} #`,
      formatComponent: (_, row) => row?.cartonProducts?.carton?.shipments?.containerId || "",
    },
    { name: "sku", title: `${translate("sku")} #` },
    { name: "description", title: translate("description") },
    {
      name: "productStocks",
      title: translate("warehouse"),
      formatText: (value: ProductStock) => value?.warehouse?.name || "",
    },
    { name: "currentStock", title: translate("total") },
    {
      name: "cartonProducts",
      title: translate("houseBill"),
      formatComponent: (_, row) => {
        return row?.cartonProducts?.carton?.shipments?.hbl || "";
      },
      key: "houseBill",
    },
    {
      name: "cartonProducts",
      title: translate("loadedStatus"),
      formatComponent: (_, row) => {
        return row?.cartonProducts?.carton?.shipments?.statusShipment || "";
      },
      key: "loadedStatus",
    },
    {
      name: "cartonProducts",
      title: translate("etd"),
      formatComponent: (_, row) =>
        renderMilestoneDateByTypeAndReference(
          [row?.cartonProducts?.carton?.shipments?.milestones ?? {}],
          MilestoneType.ESTIMATED,
          MilestoneReference.SHIPMENT.ESTIMATED.ORIGIN
        ),
      key: "etd",
    },
    {
      name: "cartonProducts",
      title: translate("storeName"),
      formatComponent: (_, row) => {
        return row?.cartonProducts?.purchaseOrder?.supplierName || "";
      },
      key: "storeName",
    },
    {
      name: "cartonProducts",
      title: translate("placeOfDestination"),
      formatComponent: (_, row) => {
        return row?.cartonProducts?.carton?.shipments?.destination || "";
      },
      key: "placeOfDestination",
    },
    {
      name: "cartonProducts",
      title: translate("mode"),
      formatComponent: (_, row) => {
        return row?.cartonProducts?.carton?.shipments?.shipmentType || "";
      },
      key: "mode",
    },
  ];

  const detailedColumns: ColumnWithKey[] = [
    { name: "sku", title: `${translate("sku")} #` },
    { name: "description", title: translate("description") },
    {
      name: "cartonProducts",
      title: `${translate("order")} #`,
      formatText: (value: CartonProduct) => value?.purchaseOrder?.reference || "",
    },
    {
      name: "cartonProducts",
      title: `${translate("container")} #`,
      formatText: (value) => value?.carton?.shipments?.containerId || "",
    },
    { name: "currentStock", title: translate("quantity") },
  ];

  const getProductReportColumns = (type: CronJobType) => {
    switch (type) {
      case CronJobType.STOCK:
        return stockColumns;
      case CronJobType.STOCK_ON_HAND:
        return stockOnHandColumns;
      case CronJobType.STOCK_ON_HAND_DETAILED:
        return detailedColumns;
      default:
        return [];
    }
  };

  return { getProductReportColumns };
};
